import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@bem-react/classname'
import countryList from 'react-select-country-list'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

import { Memo } from '@/hoc/Memo'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import useBackendError from '@/hooks/useBackendError'
import { CountryDataModel, UserModel } from '../../models/UserModel'
import { сurrencies } from '@/config'
import { currentUserSelector } from '../../store/accont.selector'
import { updateCurrentUserThunk } from '../../store/accont.slice'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'

import { Field } from '@/components/form/Field'
import { Button } from '@/components/Button'
import { Select } from '@/components/form/Select'

import './AccountForm.scss'
import useAccountValidationSchema from '@/hooks/useAccountValidationSchema'

const cnAccountForm = cn('AccountForm')

export const AccountForm: FC = Memo(() => {
  const { t, i18n, ready } = useTranslation(['translation', 'countries'])
  const dispatch = useAppDispatch()
  const [setBackendError, fieldsError] = useBackendError()
  const currentUser = useAppSelector(currentUserSelector)

  const [visibleVatField, setVisibleVatField] = useState(false)
  const [countries, setCountries] = useState<CountryDataModel[]>([])

  useEffect(() => {
    const list = countryList().getData()
    const upd = list.map(el => {
      return {
        ...el,
        label: t(`countries:${el.value}`),
      }
    })
    if (i18n.language === 'en-GB') setCountries(list)
    else setCountries(upd)
  }, [i18n, ready])

  const UserSchema = useAccountValidationSchema({})

  const {
    watch,
    reset,
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UserModel>({
    mode: 'onChange',
    resolver: yupResolver(UserSchema),
  })

  useEffect(() => {
    if (currentUser) {
      reset(currentUser)
      if (currentUser.countryCodeId === 'IT') {
        setVisibleVatField(true)
      }
    }
  }, [currentUser])

  useEffect(() => {
    const subscription = watch(value => {
      if (value.countryCodeId === 'IT') {
        setVisibleVatField(true)
      } else {
        setVisibleVatField(false)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const updateAccount = useCallback(async (data: UserModel) => {
    const sdi = data.countryCodeId === 'IT' ? data.sdi : null
    try {
      const res = await dispatch(
        updateCurrentUserThunk({
          ...data,
          sdi,
        }),
      ).unwrap()
      if (res) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t('Account details successfully updated'),
          }),
        )
      }
    } catch (errors) {
      setBackendError(errors)
    }
  }, [])

  return (
    <form
      className={cnAccountForm('form')}
      onSubmit={handleSubmit(updateAccount)}
    >
      <div className={cnAccountForm('grid')}>
        <Field
          {...register('name')}
          placeholder={t('Name')}
          label={t('Name')}
          required
          errors={
            (errors.name && errors.name.message) ||
            (fieldsError && fieldsError.name && fieldsError.name.message)
          }
        />

        <Field
          {...register('lastName')}
          placeholder={t('Last Name')}
          label={t('Last Name')}
          required
          errors={
            (errors.lastName && errors.lastName.message) ||
            (fieldsError &&
              fieldsError.lastName &&
              fieldsError.lastName.message)
          }
        />
        <Field
          {...register('companyName')}
          placeholder={t('Company Name')}
          label={t('Company Name')}
          required
          errors={
            (errors.companyName && errors.companyName.message) ||
            (fieldsError &&
              fieldsError.companyName &&
              fieldsError.companyName.message)
          }
        />

        <Select
          label={t('Country')}
          placeholder={t('(None)')}
          control={control}
          name='countryCodeId'
          options={countries}
          optionLabel='label'
          optionValue='value'
          required
          errors={
            (errors.countryCodeId && errors.countryCodeId.message) ||
            (fieldsError &&
              fieldsError.countryCodeId &&
              fieldsError.countryCodeId.message)
          }
          isNone
        />

        <Select
          label={t('Currency')}
          placeholder={t('(None)')}
          control={control}
          name='currencyId'
          options={сurrencies}
          optionLabel='name'
          optionValue='id'
          required
          errors={
            (errors.currencyId && errors.currencyId.message) ||
            (fieldsError &&
              fieldsError.customerId &&
              fieldsError.customerId.message)
          }
          isNone
        />

        <Field
          {...register('mobileTelephone')}
          placeholder={t('Mobile Phone')}
          label={t('Mobile Phone')}
          errors={
            (errors.mobileTelephone && errors.mobileTelephone.message) ||
            (fieldsError &&
              fieldsError.mobileTelephone &&
              fieldsError.mobileTelephone.message)
          }
        />
        <Field
          {...register('email')}
          placeholder={t('Email Address')}
          label={t('Email Address')}
          // required
          // errors={
          //   (errors.email && errors.email.message) ||
          //   (fieldsError && fieldsError.email && fieldsError.email.message)
          // }

          disabled
        />

        <Field
          {...register('companyAddress')}
          placeholder={t('Company Address')}
          label={t('Company Address')}
          required
          errors={
            (errors.companyAddress && errors.companyAddress.message) ||
            (fieldsError &&
              fieldsError.companyAddress &&
              fieldsError.companyAddress.message)
          }
        />

        <Field
          {...register('vat')}
          placeholder={t('Vat')}
          label={t('Vat')}
          required
          errors={
            (errors.vat && errors.vat.message) ||
            (fieldsError &&
              fieldsError.companyAddress &&
              fieldsError.companyAddress.message)
          }
        />

        {visibleVatField && (
          <Field
            {...register('sdi')}
            placeholder={t('SDI/pec')}
            label={t('SDI/pec')}
            required
            errors={
              (errors.sdi && errors.sdi.message) ||
              (fieldsError &&
                fieldsError.companyAddress &&
                fieldsError.companyAddress.message)
            }
          />
        )}
      </div>
      <div className={cnAccountForm('btn')}>
        <Button
          className={cnAccountForm('button')}
          type='submit'
          isLoading={isSubmitting}
        >
          {t('Submit changes')}
        </Button>
      </div>
    </form>
  )
})
